import React, { useEffect, useState } from "react"
import { setAuthorizationToken } from "../../../../../apis/base"

import CouponsAPI from "../../../../../apis/CouponsAPI"
import { redeemGiftCard } from "../../../../../apis/GiftCardsAPI"
import {
  ADD_COUPON,
  getCart,
  REMOVE_COUPON,
} from "../../../../../stores/CartStateStore"
import BrowserLocationHelper from "../../../../../utils/browser_location_helper"
import AvailableCoupon from "./AvailableCoupon"
import SuccessModal from "../../../../../RedeemGiftCard/SuccessModal"

import styles from "./CouponForm.module.scss"

export default function CouponForm(props) {
  // -------------------------------------
  // Props destructuring
  // -------------------------------------

  const {
    cartState,
    dispatchCartState,
    initialErrors,
    userAuthToken,
    startTime,
    venueId,
    availableCoupons,
    acceptsCoupons,
  } = props

  // -------------------------------------
  // Hooks (e.g. useState, useMemo ...)
  // -------------------------------------

  let error_messages = []
  if (initialErrors?.coupon_code) {
    error_messages = initialErrors.coupon_code.map((error) => {
      return error
    })
  }

  const [state, setState] = useState({
    coupons: [...availableCoupons],
    error_messages: error_messages,
  })

  const [inputValue, setInputValue] = useState("")
  const [giftCard, setGiftCard] = useState(null)

  // -------------------------------------
  // Effects
  // -------------------------------------

  useEffect(() => {
    CouponsAPI.SetUserAuthToken(userAuthToken)
    setAuthorizationToken(userAuthToken)
  }, [])

  useEffect(() => {
    startCouponVerification(cartState.couponDetails?.code)
  }, [cartState.deliveryDetails])

  useEffect(() => {
    if (inputValue === "") {
      invalidateCoupon()
    }
  }, [inputValue])

  // -------------------------------------
  // Component functions
  // -------------------------------------

  async function validateCoupon(code) {
    if (!code || code.length === 0) {
      setState({
        ...state,
        error_messages: [
          I18n.t("pages.orders.new.coupon_messages.default_error"),
        ],
      })
      return
    }

    const amount = getCart(cartState).subtotal || 0

    let coupon = await CouponsAPI.CheckCoupon(code, amount, venueId, startTime)

    if (coupon?.errors) {
      validateGiftCard(code, coupon?.errors)
      return
    }

    dispatchCartState({
      type: ADD_COUPON,
      payload: { code: coupon.code, amount: coupon.discount_value },
    })

    let newCoupons = state.coupons
    if (!state.coupons.some((c) => c.code === code)) {
      newCoupons = [coupon, ...newCoupons]
    }

    setState({ coupons: newCoupons, error_messages: [] })
  }

  async function validateGiftCard(code, couponErrors) {
    let { response, errors } = await redeemGiftCard(code)

    if (errors) {
      invalidateCoupon(couponErrors, true)
      return
    }

    setGiftCard(response)
  }

  function invalidateCoupon(errors) {
    dispatchCartState({
      type: REMOVE_COUPON,
    })
    setState({ ...state, error_messages: errors || [] })
  }

  function startCouponVerification(code) {
    if (code) {
      validateCoupon(code)
    } else {
      invalidateCoupon()
    }
  }

  function isVerifyButtonDisabled() {
    const minValidLength = 3
    return (
      (cartState.couponDetails?.code &&
        typeof cartState.couponDetails.code !== "string") ||
      cartState.couponDetails?.code.length < minValidLength
    )
  }

  function reloadPage() {
    BrowserLocationHelper.ReloadPage()
  }

  function renderSelectedCouponCode() {
    return (
      <div className={styles.selectedCodeContainer}>
        <span className="fr-p2 fr-normal fr-light">
          {I18n.t("pages.orders.new.selected_coupon", {
            code: cartState.couponDetails?.code,
          })}
        </span>
        <a
          onClick={() => {
            setInputValue("")
            invalidateCoupon()
          }}
        >
          <i className="fal fa-trash-alt fr-p1 fr-light" />
        </a>
      </div>
    )
  }
  function renderCouponForm() {
    return (
      <>
        <div className="input-group">
          <input
            type="text"
            name="coupon_code"
            className="form-control"
            value={cartState.couponDetails?.code}
            onChange={(event) => {
              setInputValue(event.target.value || "")
            }}
          />
          <span className="input-group-btn">
            <button
              className="btn btn-primary"
              style={{ lineHeight: "36px" }}
              type="button"
              disabled={isVerifyButtonDisabled()}
              onClick={() => startCouponVerification(inputValue)}
            >
              {I18n.t("verify")}
            </button>
          </span>
        </div>
        {renderErrors()}
      </>
    )
  }

  function renderCouponInput() {
    return cartState.couponDetails?.code
      ? renderSelectedCouponCode()
      : renderCouponForm()
  }

  function renderAvailableCoupons(coupons) {
    const count = coupons.length

    return (
      <div>
        <p className="fr-p2 fr-normal fr-margin-top-large">
          {count === 1 ? I18n.t("pages.orders.new.your_coupons_one") : I18n.t("pages.orders.new.your_coupons", {count})}
        </p>
        <div className={styles.availableCouponsContainer}>
          {coupons.map((coupon) => {
            return (
              <AvailableCoupon
                {...coupon}
                key={coupon.code}
                onClick={() => startCouponVerification(coupon.code)}
                isSelected={coupon.code === cartState.couponDetails?.code}
              />
            )
          })}
        </div>
      </div>
    )
  }

  function renderErrors() {
    if (state.error_messages.length === 0) {
      return null
    }

    return (
      <div className="help-block error">
        <ul>
          {state.error_messages.map(function (error, i) {
            return <li key={i}>{error}</li>
          })}
        </ul>
      </div>
    )
  }

  // -------------------------------------
  // Component local variables
  // -------------------------------------

  // -------------------------------------

  return (
    <div>
      <fieldset>
        <div className="row">
          <div className="col-sm-12">
            <div className="form-group">
              <p className="fr-p2 fr-normal fr-margin-top-large">
                {I18n.t("cart_summary.promo_code")}
              </p>
              {acceptsCoupons && (
                <div>
                  {renderCouponInput()}
                  {state.coupons &&
                    state.coupons.length > 0 &&
                    renderAvailableCoupons(state.coupons)}
                </div>
              )}
              {!acceptsCoupons && (
                <p>
                  {" "}
                  {I18n.t(
                    "pages.orders.new.coupon_messages.venue_disabled"
                  )}.{" "}
                </p>
              )}
            </div>
          </div>
        </div>
      </fieldset>
      <SuccessModal
        show={!!giftCard}
        onHide={reloadPage}
        onConfirm={reloadPage}
        giftCard={giftCard}
        closeLabel={I18n.t("pages.gift_cards.redeem.modal_reload")}
      />
    </div>
  )
}
